import { IonCol, IonGrid, IonRow, IonText } from "@ionic/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import PrimaryButton from "@components/buttons/PrimaryButton";
import SecondaryButton from "@components/buttons/SecondaryButton";
import DateAndTimeRange from "@components/dates/DateAndTimeRange";
import LegalDocumentForm from "@components/forms/LegalDocumentForm";
import styles from "@components/LegalDocumentTracker.module.css";
import ScheduleConsultButton from "@components/ScheduleConsultButton";
import ScheduleConsultLink from "@components/ScheduleConsultLink";
import TrackerModal from "@components/TrackerModal";
import InternalLink from "@components/typography/InternalLink";
import useBackOrNext from "@hooks/useBackOrNext";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import useMyClient from "@hooks/useMyClient";
import { Opener } from "@hooks/useOpener";
import {
  CareProfileFragment as CareProfile,
  CareProfileUseCaseEnum,
  ConsultFragment as Consult,
  LegalDocumentFragment,
  LegalDocumentKindEnum,
  TrackerStatusEnum,
  TrackerStatusUpdateDocument,
  TrackerStatusUpdateMutation,
  TrackerStatusUpdateMutationVariables,
  TrackerTypeEnum
} from "@typing/Generated";
import { journeyRoute } from "@utils/routeUtils";
import { consultEndsInFuture } from "@utils/timeUtils";

type Journey = {
  careProfile: Pick<CareProfile, "useCase">;
  consults: Consult[];
  id: string;
  legalDocuments: LegalDocumentFragment[];
  legalTrackerStatus: TrackerStatusEnum;
};
type Props = {
  journey: Journey;
  legalDocumentsOpener: Opener;
};

const LegalDocumentTracker = ({ journey, legalDocumentsOpener }: Props) => {
  const [questionIndex, setQuestionIndex] = useState<number | null>(null);
  const myClient = useMyClient();

  const questionOrder = useMemo(() => {
    const answer = [
      LegalDocumentKindEnum.DPOA_FINANCES,
      LegalDocumentKindEnum.DPOA_HEALTH_CARE,
      LegalDocumentKindEnum.LIVING_WILL,
      LegalDocumentKindEnum.TRUST,
      LegalDocumentKindEnum.WILL
    ];

    if (
      journey.careProfile.useCase &&
      [CareProfileUseCaseEnum.CHILD, CareProfileUseCaseEnum.INFANT].includes(journey.careProfile.useCase)
    ) {
      answer.push(LegalDocumentKindEnum.GUARDIAN_TRUST);
      answer.push(LegalDocumentKindEnum.GUARDIAN_WILL);
    }
    return answer;
  }, [journey.careProfile.useCase]);

  const [updateTrackerStatus] = useMutationWithValidation<
    TrackerStatusUpdateMutation,
    TrackerStatusUpdateMutationVariables
  >(TrackerStatusUpdateDocument, "tracker");

  const [completeTracker] = useMutationWithValidation<
    TrackerStatusUpdateMutation,
    TrackerStatusUpdateMutationVariables
  >(TrackerStatusUpdateDocument, "tracker", {
    update: store => {
      store.modify({
        fields: {
          legalTrackerStatus() {
            return TrackerStatusEnum.COMPLETED;
          }
        },
        id: store.identify({ ...journey })
      });
    }
  });

  useEffect(() => {
    if (questionIndex === null) {
      if (journey.legalDocuments.length === 0) {
        setQuestionIndex(0);
      } else if (journey.legalDocuments.length <= questionOrder.length) {
        setQuestionIndex(0);
      } else {
        legalDocumentsOpener.close();
      }
    }
  }, [journey.legalDocuments, legalDocumentsOpener, questionIndex, questionOrder.length]);

  const backAllowed = useMemo(() => {
    if (questionIndex && questionIndex > 0) {
      return true;
    }
    return false;
  }, [questionIndex]);

  const handleBack = useCallback(() => {
    if (backAllowed && questionIndex) {
      setQuestionIndex(questionIndex - 1);
    }
  }, [backAllowed, questionIndex]);

  const onUpdate = useCallback(() => {
    if (questionIndex !== null) {
      if (
        journey.legalDocuments.length === questionOrder.length &&
        journey.legalTrackerStatus !== TrackerStatusEnum.COMPLETED
      ) {
        completeTracker({
          journeyId: journey.id,
          status: TrackerStatusEnum.COMPLETED,
          type: TrackerTypeEnum.LEGAL_DOCUMENTS
        });
      } else if (journey.legalTrackerStatus === TrackerStatusEnum.NOT_STARTED) {
        updateTrackerStatus({
          journeyId: journey.id,
          status: TrackerStatusEnum.STARTED,
          type: TrackerTypeEnum.LEGAL_DOCUMENTS
        });
      }
      setQuestionIndex(questionIndex + 1);
    }
  }, [
    completeTracker,
    journey.id,
    journey.legalDocuments.length,
    journey.legalTrackerStatus,
    questionIndex,
    questionOrder.length,
    updateTrackerStatus
  ]);

  const setBackOrNext = useBackOrNext({ handleBack, onUpdate });

  return (
    <TrackerModal
      backAllowed={backAllowed}
      data-test="legal-document-tracker-modal"
      handleBack={handleBack}
      headerKey="pages.client.legalDocuments.sections.header"
      hideCurrentSection={questionIndex === 0 || questionIndex === questionOrder.length + 1}
      hideNext
      name="LegalDocumentTracker"
      opener={legalDocumentsOpener}
      questionIndex={questionIndex ? questionIndex - 1 : questionIndex}
      sectionCount={questionOrder.length}
    >
      <>
        {questionIndex !== null &&
          (questionIndex === 0 ? (
            <div className={styles.conclusion}>
              <p>
                <IonText>
                  <FormattedMessage id="pages.client.legalDocuments.trackAndStore" />
                </IonText>
              </p>
              <p>
                <IonText>
                  <FormattedMessage id="pages.client.legalDocuments.whatIsAsked" />
                </IonText>
              </p>
              <div className={styles.buttonSpacing}>
                <PrimaryButton expand="block" onClick={onUpdate} textKey="pages.client.legalDocuments.getStarted" />
              </div>
            </div>
          ) : questionIndex === questionOrder.length + 1 ? (
            <div className={styles.conclusion}>
              <p>
                <FormattedMessage id="pages.client.legalDocuments.review.thankYou" />
              </p>
              <div>
                <FormattedMessage
                  id="pages.client.legalDocuments.review.expertReview"
                  values={{
                    scheduleConsult: (
                      <ScheduleConsultLink
                        journeyId={journey.id}
                        translationId="pages.client.legalDocuments.review.scheduleConsult"
                      />
                    ),
                    sendMessage: (
                      <InternalLink
                        to={journeyRoute({ journeyId: journey.id, linkAfterJourney: "/messages", myClient })}
                      >
                        <FormattedMessage id="pages.client.legalDocuments.review.sendMessage" />
                      </InternalLink>
                    )
                  }}
                />
              </div>
              {journey.consults.map(
                consult =>
                  consultEndsInFuture(consult) && (
                    <div className={styles.consult} key={consult.id}>
                      <div>
                        <IonText className={styles.consultName}>
                          <FormattedMessage id="models.consult.genericName" />
                        </IonText>
                      </div>
                      <div>
                        <IonText>
                          <DateAndTimeRange endTime={consult.endTime} startTime={consult.startTime} />
                        </IonText>
                      </div>
                      <div>
                        <IonText>
                          <FormattedMessage
                            id="pages.client.medications.review.expert"
                            values={{ firstName: consult.expert.firstName }}
                          />
                        </IonText>
                      </div>
                    </div>
                  )
              )}
              <IonGrid>
                <IonRow>
                  <IonCol>
                    <ScheduleConsultButton
                      expand="block"
                      journeyId={journey.id}
                      messageKey="pages.client.legalDocuments.review.scheduleConsultButton"
                    />
                  </IonCol>
                  <IonCol>
                    <SecondaryButton
                      data-test="complete"
                      expand="block"
                      onClick={legalDocumentsOpener.close}
                      textKey="pages.client.medications.review.close"
                    />
                  </IonCol>
                </IonRow>
              </IonGrid>
            </div>
          ) : (
            <LegalDocumentForm
              backDisabled={!backAllowed}
              inTracker
              journeyId={journey.id}
              kindOptions={[questionOrder[questionIndex - 1]]}
              legalDocument={journey.legalDocuments.find(
                legalDocument => legalDocument.kind === questionOrder[questionIndex - 1]
              )}
              messageKey={`pages.client.legalDocuments.messages.${questionOrder[questionIndex - 1]}`}
              onSuccess={onUpdate}
              setBackOrNext={setBackOrNext}
              titleKey={`models.legalDocument.kindOptions.${questionOrder[questionIndex - 1]}`}
            />
          ))}
      </>
    </TrackerModal>
  );
};

export default LegalDocumentTracker;
